<template>
  <div
    class="lg!bg-blue sticky top-0 z-2 py-3 transition-colors duration-300 lg:py-5"
    :class="isActive ? 'bg-white' : 'bg-blue'"
  >
    <div class="container flex items-center justify-between gap-2">
      <div class="flex flex-col">
        <span
          class="text-sm lg:!text-blue-100"
          :class="isActive ? 'text-neutral-500' : 'text-blue-100'"
          >{{ data.productCategory }}</span
        >
        <span class="text-xl lg:!text-white" :class="isActive ? 'text-blue-950' : 'text-white'">{{
          data.productTitle
        }}</span>
      </div>
      <nav
        class="absolute left-0 top-[72px] h-[100dvh] w-full flex-col gap-4 overflow-scroll px-4 pb-48 pt-3 text-blue-950 lg:static lg:!inline-flex lg:h-auto lg:w-fit lg:flex-row lg:items-center lg:gap-6 lg:overflow-auto lg:!bg-blue lg:px-0 lg:pb-0 lg:pt-0 lg:text-white"
        :class="isActive ? 'inline-flex bg-white opacity-100' : 'hidden'"
      >
        <template v-for="(item, index) in data.navigation" :key="index">
          <component
            :is="item.slug ? NuxtLink : 'button'"
            v-if="(index !== 2 || item.slug) && item.visible"
            :to="item.slug ? item.slug : undefined"
            class="w-fit font-medium lg:font-normal"
            :class="index !== activeIndex || index === 2 ? 'lg:text-white' : 'lg:text-blue-100'"
            :download="item.slug && item.description ? item.description : undefined"
            :target="item.slug ? '__blank' : undefined"
            @click="handleNavClick(item, index)"
          >
            {{ item.title }}
          </component>
        </template>

        <Button
          variant="outline"
          class="mt-auto !h-12 w-fit !text-blue lg:!text-white"
          icon-circle-class="!h-8 !w-8 !ml-3"
          :icon="!isDesktop"
          @click="handleButtonClick"
        >
          {{ data.button.text }}
        </Button>
      </nav>

      <button
        class="h-12 w-12 rounded-full transition-colors duration-300 lg:hidden"
        :class="isActive ? 'bg-light-gray' : 'bg-orange'"
        @click="isActive = !isActive"
      >
        <Icon
          :color="isActive ? theme.colors.blue.DEFAULT : 'white'"
          :name="isActive ? 'close' : 'info'"
        />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { theme } from '#tailwind-config'

const NuxtLink = resolveComponent('NuxtLink')
const breakpoints = useBreakpoints(theme.screens)
const isScreenLg = breakpoints.greater('lg')
const { isDesktop } = useDevice()

export interface ProgramProductNavigation {
  productCategory: string
  productTitle: string
  navigation: [
    {
      title: string
      slug: string
      description?: string
      visible: boolean
    }
  ]
  button: {
    text: string
  }
}

const { bodyLock, bodyUnlock } = useBodyLock()

const emit = defineEmits(['user-action:tab-switch', 'user-action:button-click'])
defineProps({
  data: {
    type: Object as PropType<ProgramProductNavigation>,
    default: () => {}
  },
  activeIndex: {
    type: Number,
    default: 0
  }
})

const isActive = ref(false)

watch(isActive, () => {
  isActive.value ? bodyLock() : bodyUnlock()
})

function handleNavClick(navItem: { title: string; slug: string }, index: number) {
  isActive.value = false

  if (!navItem.slug) {
    emit('user-action:tab-switch', index, navItem.title)
  }

  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function handleButtonClick() {
  isActive.value = false
  emit('user-action:button-click')
}

const handleResize = useDebounceFn(() => {
  if (isScreenLg.value && isActive.value) {
    isActive.value = false
  }
}, 400)

onMounted(() => {
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>
